import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Chip, Collapse, Stack, Typography } from '@mui/material'
import { alpha, Theme, useTheme } from '@mui/material/styles'
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

import { HDSTETH_DEVICE_LABEL } from '@/hooks/useHDStethDevice'
import { useHDStethEcgValue } from '@/hooks/useHDStethEcgValue'
import type { HDStethEcgSnapshot, HDStethEcgSource } from '@/utils/hdSteth/ecg-types'

const WINDOW_SECONDS = 5
const CHART_HEIGHT = 120
const CHART_PADDING = 12
const GRID_COLUMNS = WINDOW_SECONDS * 5
const GRID_ROW_SPACING = 20

type DisplayStatus = 'live' | 'waiting' | 'disconnected'

interface StripColors {
  trace: string
  majorGrid: string
  minorGrid: string
}

function getDisplayStatus(snapshot: HDStethEcgSnapshot | undefined): DisplayStatus {
  if (!snapshot || snapshot.status === 'disconnected') return 'disconnected'
  return snapshot.status === 'live' && snapshot.samples.length > 1 ? 'live' : 'waiting'
}

function isSimulatedSnapshot(snapshot: HDStethEcgSnapshot | undefined): boolean {
  return snapshot?.simulated === true
}

/** Optional HD Steth strip; expanding it never changes acquisition or audio. */
export function HDStethEcgPanel({ source }: { source: HDStethEcgSource }): JSX.Element {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [panelId] = useState<string>(() => `hd-steth-ecg-${crypto.randomUUID()}`)
  const status = useHDStethEcgValue(source, getDisplayStatus)
  const isSimulated = useHDStethEcgValue(source, isSimulatedSnapshot)
  const isLive = status === 'live'

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.25, flexWrap: 'wrap' }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <MonitorHeartOutlinedIcon sx={{ fontSize: 19, color: 'primary.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{HDSTETH_DEVICE_LABEL}</Typography>
          {isSimulated && <Chip size="small" color="warning" variant="outlined" label={t('hdStethEcg.simulated')} />}
        </Stack>
        <Button
          size="small"
          endIcon={isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          aria-expanded={isExpanded}
          aria-controls={panelId}
          onClick={() => setIsExpanded((value) => !value)}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {t(isExpanded ? 'hdStethEcg.hide' : 'hdStethEcg.show')}
        </Button>
      </Stack>
      <Collapse in={isExpanded} unmountOnExit>
        <Box id={panelId} role="region" aria-label={t<string>('hdStethEcg.title')} sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{t('hdStethEcg.title')}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.75} role="status">
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isLive ? 'success.main' : 'text.disabled' }} />
              <Typography variant="caption" sx={{ color: isLive ? 'success.dark' : 'text.secondary' }}>{t(`hdStethEcg.${status}`)}</Typography>
            </Stack>
          </Stack>
          <Box sx={{ position: 'relative', border: 1, borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden', bgcolor: 'background.paper' }}>
            <EcgStrip source={source} />
            {!isLive && (
              <Stack sx={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center', bgcolor: (theme: Theme) => alpha(theme.palette.background.paper, 0.92) }}>
                <Typography variant="body2" color="text.secondary">{t(`hdStethEcg.${status}`)}</Typography>
              </Stack>
            )}
          </Box>
          <Stack direction="row" justifyContent="space-between" gap={1} sx={{ mt: 0.75 }}>
            <Typography variant="caption" color="text.secondary">{t('hdStethEcg.window', { seconds: WINDOW_SECONDS })}</Typography>
            <Typography variant="caption" color="text.secondary">{t('hdStethEcg.relativeAmplitude')}</Typography>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  )
}

/** Draws straight from the store at most once per animation frame; React never re-renders per packet. */
function EcgStrip({ source }: { source: HDStethEcgSource }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const { t } = useTranslation()
  const theme = useTheme()

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const colors: StripColors = {
      trace: theme.palette.primary.main,
      majorGrid: alpha(theme.palette.primary.main, 0.18),
      minorGrid: alpha(theme.palette.primary.main, 0.08),
    }
    let frame = 0
    const scheduleDraw = (): void => {
      if (frame) return
      frame = window.requestAnimationFrame(() => {
        frame = 0
        drawStrip(canvas, source.getSnapshot(), colors)
      })
    }
    const observer = new ResizeObserver(scheduleDraw)
    observer.observe(canvas)
    const unsubscribe = source.subscribe(scheduleDraw)
    scheduleDraw()
    return () => {
      window.cancelAnimationFrame(frame)
      unsubscribe()
      observer.disconnect()
    }
  }, [source, theme])

  return <Box component="canvas" ref={canvasRef} role="img" aria-label={t<string>('hdStethEcg.title')} sx={{ display: 'block', width: '100%', height: CHART_HEIGHT }} />
}

function drawStrip(canvas: HTMLCanvasElement, ecg: HDStethEcgSnapshot | undefined, colors: StripColors): void {
  const width = canvas.getBoundingClientRect().width
  const ratio = window.devicePixelRatio || 1
  canvas.width = Math.round(width * ratio)
  canvas.height = CHART_HEIGHT * ratio
  const context = canvas.getContext('2d')
  if (!context || width === 0) return
  context.scale(ratio, ratio)
  drawGrid(context, width, colors)
  if (!ecg || getDisplayStatus(ecg) !== 'live' || !Number.isFinite(ecg.sampleRateHz) || ecg.sampleRateHz <= 0) return
  drawTrace(context, width, ecg, colors.trace)
}

function drawTrace(context: CanvasRenderingContext2D, width: number, ecg: HDStethEcgSnapshot, color: string): void {
  const count = Math.round(ecg.sampleRateHz * WINDOW_SECONDS)
  const samples = ecg.samples.slice(-count)
  const max = ecg.mode === '12-bit' ? 4095 : 63
  context.beginPath()
  context.strokeStyle = color
  context.lineWidth = 1.6
  context.lineJoin = 'round'
  samples.forEach((sample, index) => {
    const x = ((count - samples.length + index) / Math.max(1, count - 1)) * width
    const y = CHART_HEIGHT - CHART_PADDING - (sample / max) * (CHART_HEIGHT - 2 * CHART_PADDING)
    if (index === 0) context.moveTo(x, y)
    else context.lineTo(x, y)
  })
  context.stroke()
}

function drawGrid(context: CanvasRenderingContext2D, width: number, colors: StripColors): void {
  context.lineWidth = 0.5
  for (let column = 0; column <= GRID_COLUMNS; column += 1) {
    context.strokeStyle = column % 5 === 0 ? colors.majorGrid : colors.minorGrid
    const x = (column / GRID_COLUMNS) * width
    context.beginPath()
    context.moveTo(x, 0)
    context.lineTo(x, CHART_HEIGHT)
    context.stroke()
  }
  context.strokeStyle = colors.minorGrid
  for (let y = GRID_ROW_SPACING; y < CHART_HEIGHT; y += GRID_ROW_SPACING) {
    context.beginPath()
    context.moveTo(0, y)
    context.lineTo(width, y)
    context.stroke()
  }
}
